///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { _isEqual } from '../utils';
import { E2eTestUtils } from '../../../testing/e2e-test-utils';
import { NgClass } from '@angular/common';
import { SuiSvgIconComponent, SuiTooltipDirective, SuiTranslatePipe } from '@siklu/sui-lib';
import { ButtonsDirectivesModule } from '../directives/buttons-directives/buttons-directives.module';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'winde-custom-list-box',
    templateUrl: './custom-list-box.component.html',
    styleUrls: ['./custom-list-box.scss'],
    imports: [NgClass, FormsModule, SuiSvgIconComponent, SuiTooltipDirective, SuiTranslatePipe, ButtonsDirectivesModule]
})
export class GtthCustomListBoxComponent implements OnInit, OnChanges, OnDestroy {
    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();

    @Input() isReadonlyMode = false;
    @Input() availableList: any[];
    @Input() displayName: string;
    @Input() selectedList: any[];
    @Input() hasIncluded = false;
    @Input() includedItem: any;
    @Input() isRequired: boolean;
    @Input() isValid: boolean;
    @Input() showValidationText: boolean;

    @Output() includedItemChange = new EventEmitter();
    @Output() selectedListChange = new EventEmitter();
    @Output() isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    checkedAvailable = [];
    checkedSelected = [];
    availableListCopy = [];
    dragListFrom: any[];
    dragListTo: any[];
    dragElOver;
    searchAvailableVal: string;
    includedItemId: number;
    isShowError: boolean;
    validationMessage = '';
    private listValidationTimeout;
    private readonly itemDragOverClass = 'item-drag-over';
    private readonly itemDragOverSelector = `.${this.itemDragOverClass}`;

    ngOnInit() {
        this.displayName = this.displayName || 'name';
        this.includedItemId = this.includedItem ? this.includedItem.id : undefined;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedList && !_isEqual(changes.selectedList.currentValue, changes.selectedList.previousValue)) {
            if (this.listValidationTimeout) {
                clearTimeout(this.listValidationTimeout);
            }
            this.listValidationTimeout = setTimeout(() => {
                this.selectListValidation();
            }, 0);
        }

        this.getAvalibleList();

        if (this.showValidationText) {
            this.isShowError = this.showValidationText;
        }
    }

    ngOnDestroy() {
        if (this.listValidationTimeout) {
            clearTimeout(this.listValidationTimeout);
            this.listValidationTimeout = null;
        }
    }

    toggleSelect(list: any[], item) {
        const index = this.checkInList(list, item);
        if (index === -1) {
            list.push(item);
        } else {
            list.splice(index, 1);
        }
    }

    checkInList(list: any[], item) {
        return list.findIndex((el) => {
            return el.id === item.id;
        });
    }

    getItemById(list, itemId) {
        return list.find((item) => {
            return item.id === itemId;
        });
    }

    listItemsAction(action: string, moveAll = false) {
        switch (action) {
            case 'copy':
                if (moveAll) {
                    this.checkedAvailable = [...this.availableListCopy];
                }
                this.checkedAvailable.forEach((item) => {
                    this.copyItem(this.availableListCopy, this.selectedList, item);
                });
                this.checkedAvailable = [];
                break;
            case 'remove':
                if (moveAll) {
                    this.checkedSelected = [...this.selectedList];
                }
                this.checkedSelected.forEach((item) => {
                    this.copyItem(this.selectedList, this.availableListCopy, item);
                });
                this.checkedSelected = [];
                break;
        }
    }

    copyItem(listFrom: any[], listTo: any[], item, position?: number) {
        const currentPosition = this.checkInList(listTo, item);
        if (currentPosition === -1) {
            if (position || position === 0) {
                listTo.splice(position, 0, item);
            } else {
                listTo.push(item);
            }
            const fromIndex = this.checkInList(listFrom, item);
            listFrom.splice(fromIndex, 1);
        } else if (position || position === 0) {
            const positionDelta = position > currentPosition ? -1 : 0;
            listTo.splice(currentPosition, 1);
            listTo.splice(position + positionDelta, 0, item);
        } else {
            listTo.push(item);
            listTo.splice(currentPosition, 1);
        }

        this.selectListValidation();
        this.selectedListChange.emit(this.selectedList);
    }

    dragOver(event) {
        event.preventDefault();
        const elOver = this.closestElement(event.target, '.custom-list__item');
        if (!elOver) {
            return;
        }
        this.dragElOver = this.dragElOver ?? elOver;
        if (this.dragElOver && this.dragElOver !== elOver) {
            this.dragElOver.classList.remove(this.itemDragOverClass);
            this.dragElOver = elOver;
            this.dragElOver.classList.add(this.itemDragOverClass);
        }
    }

    drag(event, item) {
        event.dataTransfer.setData('itemId', item.id);
        this.dragListFrom = this.getDragTargetList(event);
    }

    dragEnd(_event) {
        [].forEach.call(document.querySelectorAll(this.itemDragOverSelector), (el) => {
            el.classList.remove(this.itemDragOverClass);
        });
    }

    drop(event) {
        event.preventDefault();
        this.dragListTo = this.getDragTargetList(event);
        const itemId = event.dataTransfer.getData('itemId');
        const item = this.getItemById(this.dragListFrom, Number(itemId));
        if (event.target.classList.contains('custom-list__box')) {
            this.copyItem(this.dragListFrom, this.dragListTo, item);
        } else {
            const overElement = this.closestElement(event.target, '.custom-list__item');
            overElement.classList.remove(this.itemDragOverClass);
            const overElementId = parseInt(overElement.id.split('-')[1], 10);
            const overItem = this.getItemById(this.dragListTo, overElementId);
            const overItemIndex = this.checkInList(this.dragListTo, overItem);
            this.copyItem(this.dragListFrom, this.dragListTo, item, overItemIndex + 1);
        }
        [].forEach.call(document.querySelectorAll(this.itemDragOverSelector), (el) => {
            el.classList.remove(this.itemDragOverClass);
        });
    }

    getDragTargetList(event) {
        const listBlock = this.closestElement(event.target, '.custom-list__box');
        if (listBlock.classList.contains('custom-list__box--available')) {
            return this.availableListCopy;
        } else {
            return this.selectedList;
        }
    }

    getAvalibleList() {
        this.availableListCopy = this.availableList.filter((item) => {
            if (this.includedItem) {
                return this.checkInList(this.selectedList, item) === -1 && this.includedItem.id !== item.id;
            } else {
                return this.checkInList(this.selectedList, item) === -1;
            }
        });
    }

    searchInAvailableList() {
        this.checkedAvailable = [];
        this.getAvalibleList();
        if (this.searchAvailableVal && this.searchAvailableVal !== '') {
            const searchRegexp = new RegExp(this.searchAvailableVal, 'gi');
            const tempList = this.availableListCopy.filter((item) => {
                return item[this.displayName].search(searchRegexp) !== -1;
            });
            this.availableListCopy = tempList;
        }
    }

    changeIncluded() {
        this.includedItem = this.availableList.find((item) => {
            return item.id === this.includedItemId;
        });
        this.includedItemChange.emit(this.includedItem);
        this.getAvalibleList();
        const includedIndex = this.selectedList.findIndex((item) => {
            return this.includedItem && item.id === this.includedItem.id;
        });
        if (includedIndex !== -1) {
            this.selectedList.splice(includedIndex, 1);
        }
    }

    closestElement(element: any, selector: string) {
        const matchesFn = ['matches', 'webkitMatchesSelector', 'mozMatchesSelector', 'msMatchesSelector', 'oMatchesSelector'].find(
            function (fn) {
                return typeof document.body[fn] === 'function';
            }
        );

        if (matchesFn) {
            while (element) {
                if (element[matchesFn](selector)) {
                    return element;
                }
                element = element.parentElement;
            }
        }

        return null;
    }

    private selectListValidation() {
        if (this.isRequired && (!this.selectedList || this.selectedList.length === 0)) {
            this.validationMessage = 'Field is required';
            this.isValid = false;
        } else {
            this.validationMessage = '';
            this.isValid = true;
        }
        this.isValidChange.emit(this.isValid);
    }
}
